import React from "react";

import Fuse from "fuse.js";

import {
  DataTypeProvider,
  FilteringState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSelection,
  IntegratedSorting,
  PagingState,
  RowDetailState,
  SelectionState,
  SortingState,
} from "@devexpress/dx-react-grid";
import {
  Table as DevExpressTable,
  Grid,
  PagingPanel,
  TableColumnVisibility,
  TableRowDetail,
  TableSelection,
} from "@devexpress/dx-react-grid-material-ui";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core";
import { Clear } from "@material-ui/icons";

import { capitalizeText, compareDates } from "@ip-utils";

import { Loader } from "../index";
import Cell from "./Cell";
import Filter from "./Filter";
import Header from "./Header";
import Row from "./Row";

/**
 * The Table has two ways of displaying; compact view and normal.
 * The compact view will display the data in two columns for each row; so a table within this table.
 * Both use the same logic for searching, filtering, sorting and pagination.
 * The breakpoint will determine which view is displayed.
 */
class Table extends React.Component {
  constructor(props) {
    super(props);

    const { columns, storage, storageRaw } = props;

    this.columnExtensions = [];
    this.dataTypeProviders = [];
    this.integratedFilteringColumnExtensions = [];
    this.integratedSortingColumnExtensions = [];
    this.storage =
      storageRaw ||
      (storage && `${window.location.hash.replace("#", "")}-${storage}`);

    this.pagingSizes = [10, 25, 50, 100, 0];

    const filters = [];
    const sorting = [];

    const compares = {
      date: compareDates,
    };

    this.columns = columns.map(
      ({
        align,
        compare,
        component,
        defaultSorting,
        filter,
        width,
        wordWrap,
        ...column
      }) => {
        if (!column.title) {
          column.title = capitalizeText(column.name);
        }

        this.columnExtensions.push({
          columnName: column.name,
          align,
          width,
          wordWrapEnabled: wordWrap,
        });

        compare = compare || compares[column.name];
        if (compare) {
          this.integratedSortingColumnExtensions.push({
            columnName: column.name,
            compare,
          });
        }

        if (component) {
          this.dataTypeProviders.push({
            formatterComponent: component,
            for: [column.name],
          });
        }

        if (defaultSorting) {
          sorting.push({
            columnName: column.name,
            direction: defaultSorting,
          });
        }

        if (filter) {
          if (!filter.component) {
            filter = {
              component: filter,
            };
          }

          if (filter.component.filter) {
            this.integratedFilteringColumnExtensions.push({
              columnName: column.name,
              predicate: filter.component.filter,
            });
          }

          filters.push({
            columnName: column.name,
            value: null,
            ...filter,
          });
        }

        return column;
      }
    );

    this.columns.push({
      name: "_search_score",
    });

    this.initialState = {
      compact: false,
      expandedRowIds: [],
      filters,
      paging: {
        current: 0,
        size: this.pagingSizes[1],
      },
      search: "",
      selection: [],
      selectionIndex: [],
      sorting,
    };

    this.state = {
      ...this.initialState,
    };

    if (this.storage) {
      const stored = JSON.parse(sessionStorage.getItem(this.storage));
      if (stored) {
        if (stored.filtering) {
          this.state.filters = this.state.filters.map((filter) => {
            const filtering = stored.filtering.find(
              (filtering) => filtering.columnName === filter.columnName
            );
            if (filtering) {
              filter.value = filtering.value;
              if (filter.onChange !== undefined) {
                filter.onChange(filter.value);
              }
            }
            return filter;
          });
        }

        if (stored.paging) {
          this.state.paging = stored.paging;
        }

        if (stored.search) {
          this.state.search = stored.search;
        }

        if (stored.sorting) {
          this.state.sorting = stored.sorting;
        }
      }
    }
  }

  componentDidMount() {
    window.addEventListener("resize", this.resize);
    this.resize();
  }

  componentDidUpdate(prevProps) {
    const { rows, onChange } = this.props;

    if (
      this.state.selection.length &&
      (!rows ||
        !rows.length ||
        (prevProps.rows && prevProps.rows.length !== rows.length))
    ) {
      this.setState({
        selection: [],
        selectionIndex: [],
      });
    }

    if (onChange) {
      onChange(this.state);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resize);
  }

  resize = () => {
    const compact = window.innerWidth < (this.props.breakpoint || 960);
    if (this.state.compact !== compact) {
      this.setState({
        compact,
      });
    }
  };

  store = () => {
    if (this.storage) {
      const data = JSON.stringify({
        filtering: this.getFiltering(),
        paging: this.state.paging,
        search: this.state.search,
        sorting: this.getSorting(),
      });
      sessionStorage.setItem(this.storage, data);
    }
  };

  getFiltering = () => {
    return this.state.filters
      .filter((filter) => !!filter.value && filter.columnName !== "search")
      .map(({ component, ...filter }) => ({
        ...filter,
        operation: "equal",
      }));
  };

  getSorting = () => {
    const { search, sorting } = this.state;

    if (search) {
      sorting.unshift({
        columnName: "_search_score",
        direction: "asc",
      });
    }

    return sorting;
  };

  getSearchedRows = () => {
    const rows = this.props.rows || [];

    if (!rows.length || !this.state.search) {
      return rows;
    }

    const fuse = new Fuse(rows, {
      distance: 1000,
      includeScore: true,
      keys: Object.keys(rows[0]),
      shouldSort: true,
    });

    const results = fuse.search(this.state.search);
    const perfectMatches = results.filter((match) => !match.score);
    const filtered = perfectMatches.length ? perfectMatches : results;
    return filtered.map((match) => ({
      ...match.item,
      _search_score: match.score,
    }));
  };

  handleExpandedRowIdsChange = (expandedRowIds) => {
    this.setState({
      expandedRowIds,
    });
  };

  handleFilterChange = (e) => {
    const { name, value } = e.target;

    this.setState(
      (prevState) => ({
        expandedRowIds: [],
        filters: prevState.filters.map((filter) => {
          if (filter.columnName === name) {
            filter.value = value;
            if (filter.onChange !== undefined) {
              filter.onChange(value);
            }
          }
          return filter;
        }),
      }),
      this.store
    );
  };

  handleFilterReset = () => {
    this.setState(
      (prevState) => ({
        expandedRowIds: [],
        filters: prevState.filters.map((filter) => {
          filter.value = null;
          if (filter.onChange !== undefined) {
            filter.onChange(null);
          }
          return filter;
        }),
      }),
      this.store
    );
  };

  handlePagingChange = (current, size) => {
    if (size) {
      current = 0;
    }

    this.setState(
      (prevState) => ({
        expandedRowIds: [],
        paging: {
          current: isNaN(current) ? prevState.paging.current : current,
          size: isNaN(size) ? prevState.paging.size : size,
        },
      }),
      this.store
    );
  };

  handleSelectionChange = (selectionIndex) => {
    const { rows } = this.props;
    this.setState({
      selection: rows.filter((row, index) => selectionIndex.includes(index)),
      selectionIndex,
    });
  };

  handleSearchChange = (e) => {
    this.setState(
      {
        expandedRowIds: [],
        search: e ? e.currentTarget.value : "",
      },
      this.store
    );
  };

  handleSortingChange = (sorting) => {
    this.setState(
      {
        expandedRowIds: [],
        sorting,
      },
      this.store
    );
  };

  render() {
    const {
      classes,
      loading,
      rowDetail,
      rowLink,
      rowProps,
      rows,
      selection,
      onRowClick,
      noFilter,
    } = this.props;
    const { compact, expandedRowIds, filters, paging, search, selectionIndex } =
      this.state;

    const filtering = this.getFiltering();
    const sorting = this.getSorting();
    const searchedRows = this.getSearchedRows();

    const noDataCellComponent = (props) => (
      <td colSpan={props.colSpan}>
        <Box padding={2}>
          <Loader loading={loading}>
            <Typography variant="body1" align="center">
              {search || filtering.length ? "Nothing found" : "No Data"}
            </Typography>
          </Loader>
        </Box>
      </td>
    );

    const columnExtensions = !compact && this.columnExtensions;

    const rowComponent = ({ row, ...props }) => {
      const extraProps = rowProps && rowProps(row);
      return (
        <Row
          compact={compact}
          link={rowLink}
          row={row}
          onClick={onRowClick}
          {...props}
          {...extraProps}
        />
      );
    };

    const searchClearProps = {
      endAdornment: !!search && (
        <InputAdornment position="end">
          <IconButton size="small" onClick={() => this.handleSearchChange()}>
            <Clear />
          </IconButton>
        </InputAdornment>
      ),
    };

    return (
      <div className={`${classes.container} ${compact ? classes.compact : ""}`}>
        <Grid columns={this.columns} rows={searchedRows}>
          <FilteringState filters={filtering} />
          <PagingState
            currentPage={paging.current}
            pageSize={paging.size}
            onCurrentPageChange={(v) => this.handlePagingChange(v)}
            onPageSizeChange={(v) => this.handlePagingChange(null, v)}
          />
          <SelectionState
            selection={selectionIndex}
            onSelectionChange={this.handleSelectionChange}
          />
          <SortingState
            sorting={sorting}
            onSortingChange={this.handleSortingChange}
          />
          <RowDetailState
            expandedRowIds={expandedRowIds}
            onExpandedRowIdsChange={this.handleExpandedRowIdsChange}
          />
          <IntegratedFiltering
            columnExtensions={this.integratedFilteringColumnExtensions}
          />
          <IntegratedSorting
            columnExtensions={this.integratedSortingColumnExtensions}
          />
          {this.dataTypeProviders.map((provider, key) => (
            <DataTypeProvider key={key} {...provider} />
          ))}
          <IntegratedPaging />
          <IntegratedSelection />
          <DevExpressTable
            cellComponent={Cell}
            columnExtensions={columnExtensions}
            noDataCellComponent={noDataCellComponent}
            rowComponent={rowComponent}
          />
          <Header compact={compact} />
          <TableColumnVisibility defaultHiddenColumnNames={["_search_score"]} />
          {!noFilter && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-end"
              marginBottom={1}
              displayPrint="none"
            >
              <Box flexGrow={1}>
                <TextField
                  InputProps={searchClearProps}
                  label="Search"
                  value={search}
                  onChange={this.handleSearchChange}
                />
              </Box>
              {!!filters.length && (
                <Filter
                  filtering={filtering}
                  filters={filters}
                  rows={rows}
                  onChange={this.handleFilterChange}
                  onReset={this.handleFilterReset}
                />
              )}
            </Box>
          )}
          <PagingPanel pageSizes={this.pagingSizes} />
          {selection && <TableSelection showSelectAll />}
          {rowDetail && (
            <TableRowDetail
              contentComponent={rowDetail}
              toggleColumnWidth={40}
            />
          )}
        </Grid>
      </div>
    );
  }
}

Table.Filter = Filter;

const styles = (theme) => ({
  container: {
    "&, & table": {
      borderCollapse: "collapse",
      minWidth: "0 !important",
      width: "100%",
    },
    "& table table td:first-child:not(:last-child)": {
      fontWeight: 700,
    },
  },
  compact: {
    "& > :last-child": {
      paddingTop: theme.spacing(2),
    },
    "& colgroup": {
      display: "none",
    },
    "&, & table": {
      "& td": {
        padding: `${theme.spacing(0.5)}px`,
        verticalAlign: "top",
        whiteSpace: "normal",
        "&:first-child:not(:last-child)": {
          paddingLeft: `${theme.spacing(0.5)}px`,
        },
        "&:last-child:not(:first-child)": {
          paddingRight: `${theme.spacing(0.5)}px`,
          textAlign: "right",
        },
        "& tr:last-child td": {
          borderBottom: 0,
        },
      },
    },
  },
});

export default withStyles(styles)(Table);
